/**
 * ContractorPicker — "was a contractor involved?" for any EHS form.
 *
 * ONE component, used by incidents, observations, hazards, HIRA, audits, CAPA,
 * meetings and training, so contractor attribution looks and behaves the same
 * everywhere and no module grows its own contractor list. That is the whole
 * point of a contractor master: a form asks the master, it does not keep one.
 *
 * DESIGN NOTES
 *
 * Optional by default. Most records involve no contractor, so the picker starts
 * collapsed behind a single checkbox and adds no friction to the common case.
 * A form that is inherently about contractors can pass `alwaysOpen`.
 *
 * Site-aware. Contractors are narrowed to those engaged at the active site when
 * site assignments exist, and fall back to the full list when the tenant has
 * not started using assignments — otherwise adopting the feature would empty
 * every picker.
 *
 * Worker list follows the contractor. Choosing a firm filters the workers to
 * that firm's, because picking a worker from a different contractor is always a
 * mistake.
 *
 * Eligibility is shown, never enforced here. A worker can be attributed to an
 * incident whether or not they were eligible to be doing the work — in fact an
 * ineligible worker on an incident is exactly the finding worth recording. Only
 * PTW blocks on eligibility, and only at issue.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { HardHat } from 'lucide-react';
import EligibilityBadge from './EligibilityBadge';
import SearchableSelect from './SearchableSelect';
import {
  fetchContractors, fetchWorkers, fetchAssignments, fetchWorkerEligibility,
} from '../services/contractorApi';

export default function ContractorPicker({
  /** Currently selected contractor id (controlled). */
  contractorId = '',
  /** Currently selected worker id (controlled). Omit to hide the worker field. */
  workerId,
  /** (contractorId, workerId, {contractor, worker}) => void
   *  The third argument carries the selected ROWS, so a caller that needs a
   *  display name does not have to re-fetch the master to get one. Added last
   *  and optional, so existing callers that take two arguments are unaffected. */
  onChange,
  /** Narrow contractors to those engaged at this site. */
  siteId = null,
  /** Narrow further to those engaged for this kind of work. */
  workType = null,
  /** Show the worker select at all. */
  showWorker = true,
  /** Skip the collapsed checkbox — for a form that is about contractors. */
  alwaysOpen = false,
  /** Shown under the checkbox to explain what attribution is for. */
  hint = 'Links this record to the contractor, so it appears in their EHS '
       + 'history and counts towards their performance.',
  disabled = false,
}) {
  const [open, setOpen] = useState(alwaysOpen || Boolean(contractorId));
  const [contractors, setContractors] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [engagedIds, setEngagedIds] = useState(null);
  const [eligibility, setEligibility] = useState(null);
  const [loading, setLoading] = useState(false);

  // Master data, loaded once the section is actually opened — a collapsed
  // picker on a form nobody expands should cost nothing.
  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    setLoading(true);
    Promise.all([
      fetchContractors({ signal: controller.signal }).catch(() => ({ companies: [] })),
      showWorker
        ? fetchWorkers({ siteId }, { signal: controller.signal })
          .catch(() => ({ workers: [] }))
        : Promise.resolve({ workers: [] }),
    ])
      .then(([c, w]) => {
        setContractors((c.companies || [])
          .filter((x) => (x.status || 'active').toLowerCase() !== 'blacklisted'));
        setWorkers((w.workers || [])
          .filter((x) => (x.status || 'active').toLowerCase() === 'active'));
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [open, siteId, showWorker]);

  // Which contractors are engaged here for this work.
  useEffect(() => {
    if (!open || !siteId) { setEngagedIds(null); return undefined; }
    const controller = new AbortController();
    fetchAssignments({ siteId, workType }, { signal: controller.signal })
      .then((res) => {
        const ids = (res.assignments || []).map((a) => a.contractor_company_id);
        // No assignments anywhere means the tenant has not adopted them yet;
        // narrowing to nothing would break attribution rather than focus it.
        setEngagedIds(ids.length ? ids : null);
      })
      .catch(() => setEngagedIds(null));
    return () => controller.abort();
  }, [open, siteId, workType]);

  // Eligibility of the chosen worker — advisory context, not a gate.
  useEffect(() => {
    if (!workerId) { setEligibility(null); return undefined; }
    const controller = new AbortController();
    fetchWorkerEligibility(workerId, { siteId, workType },
                           { signal: controller.signal })
      .then(setEligibility)
      .catch(() => setEligibility(null));
    return () => controller.abort();
  }, [workerId, siteId, workType]);

  const emit = useCallback((nextContractor, nextWorker) => {
    onChange?.(nextContractor || '', nextWorker || '', {
      contractor: contractors.find((c) => c.id === nextContractor) || null,
      worker: workers.find((w) => w.id === nextWorker) || null,
    });
  }, [onChange, contractors, workers]);

  const selectable = engagedIds
    ? contractors.filter((c) => engagedIds.includes(c.id))
    : contractors;

  // A worker must belong to the chosen contractor. Without a contractor the
  // full list is offered, and picking a worker fills the contractor in.
  const selectableWorkers = contractorId
    ? workers.filter((w) => w.contractor_company_id === contractorId)
    : workers;

  // Option lists for the searchable selects. Trade and badge go into `hint`
  // and `badge` so they are searchable too: on a real worker list the term
  // someone types is as often the trade or the gate-pass number as the name.
  const contractorOptions = selectable.map((c) => ({
    value: c.id,
    label: c.name,
    // Registration number, not trade: contractor_companies has no trade column,
    // and the registration number is the other thing people search a firm by.
    hint: c.registration_no || undefined,
    badge: (c.status || '').toLowerCase() === 'active' ? undefined : c.status,
  }));
  // A previously-saved contractor that is not engaged here must stay
  // selectable, or editing an old record would silently clear it.
  if (contractorId && !selectable.some((c) => c.id === contractorId)) {
    contractorOptions.push({
      value: contractorId,
      label: contractors.find((c) => c.id === contractorId)?.name
        || 'Selected contractor',
      hint: 'Not engaged at this site',
    });
  }

  const workerOptions = selectableWorkers.map((w) => ({
    value: w.id,
    label: w.name,
    hint: [w.trade, w.contractor_company].filter(Boolean).join(' · ') || undefined,
    badge: w.badge_no || undefined,
  }));

  const handleContractor = (value) => {
    // Changing firm invalidates a worker from the previous one.
    const keepWorker = workers.find((w) => w.id === workerId
      && w.contractor_company_id === value);
    emit(value, keepWorker ? workerId : '');
  };

  const handleWorker = (value) => {
    const worker = workers.find((w) => w.id === value);
    // Picking a worker with no contractor chosen yet adopts theirs, so the two
    // fields cannot disagree about who the worker works for.
    emit(contractorId || worker?.contractor_company_id || '', value);
  };

  if (!alwaysOpen && !open) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
        <label className="flex cursor-pointer items-start gap-2.5">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={false}
            disabled={disabled}
            onChange={() => setOpen(true)}
          />
          <span>
            <span className="flex items-center gap-1.5 text-sm font-medium text-gray-800">
              <HardHat className="h-4 w-4 text-gray-500" />
              A contractor was involved
            </span>
            <span className="mt-0.5 block text-[11px] text-gray-500">{hint}</span>
          </span>
        </label>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-1.5 text-sm font-bold text-gray-800">
          <HardHat className="h-4 w-4 text-gray-500" />
          Contractor involved
        </h3>
        {!alwaysOpen && (
          <button
            type="button"
            onClick={() => { setOpen(false); emit('', ''); }}
            className="text-xs font-medium text-gray-500 hover:text-gray-700"
          >
            Not a contractor
          </button>
        )}
      </div>

      <div className={`grid grid-cols-1 gap-3 ${showWorker ? 'md:grid-cols-2' : ''}`}>
        <div>
          <label className="mb-1 block text-xs font-semibold text-gray-700"
                 htmlFor="cp-contractor">
            Contractor
          </label>
          <SearchableSelect
            id="cp-contractor"
            value={contractorId || ''}
            disabled={disabled || loading}
            placeholder={loading ? 'Loading contractors…' : '— None —'}
            searchPlaceholder="Search by name or registration…"
            options={contractorOptions}
            onChange={(next) => handleContractor(next)}
            noResultsText="No contractor matches that search"
          />
          {engagedIds && (
            <p className="mt-1 text-[11px] text-gray-500">
              Showing contractors engaged at this site
              {workType ? ` for ${workType}` : ''}.
            </p>
          )}
        </div>

        {showWorker && (
          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-700"
                   htmlFor="cp-worker">
              Worker <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <SearchableSelect
              id="cp-worker"
              value={workerId || ''}
              disabled={disabled || loading}
              placeholder={loading ? 'Loading workers…' : '— None —'}
              searchPlaceholder="Search by name, trade or badge…"
              options={workerOptions}
              onChange={(next) => handleWorker(next)}
              noResultsText={contractorId
                ? 'No worker of this contractor matches'
                : 'No worker matches that search'}
            />
            {eligibility && (
              <div className="mt-1.5">
                {/* Advisory. An ineligible worker on an incident is a finding
                    worth recording, not something to block. */}
                <EligibilityBadge result={eligibility} compact advisory />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
